/**
 * GlobalFooter — PWWCapital institutional-grade footer.
 *
 * Six mandatory sections per the Global-Standard Footer Directive:
 *   1. Corporate Identity
 *   2. Regulatory & Compliance
 *   3. Security & Fraud Prevention
 *   4. Banking Products
 *   5. Legal & Policy Documents
 *   6. Bottom Bar (copyright, region, language)
 *
 * Rules (non-negotiable per governance directive):
 *   - Do NOT change colors, spacing, or typography outside tokens.
 *   - Do NOT add icons unless approved.
 *   - All regulatory/legal text is defined in footer-config.js.
 *   - WCAG 2.2 AA compliant.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PwwLogoIcon } from '../PwwLogoIcon';
import {
  CORPORATE,
  REGULATORY_ITEMS,
  SECURITY_LINKS,
  BANKING_PRODUCTS,
  LEGAL_LINKS,
  COMPLIANCE_BADGES,
  LANGUAGES,
  REGIONS,
} from './config/footer-config';
import './GlobalFooter.css';

/* ── Sub-section components ─────────────────────────────────────────────── */

/** Section 1: Corporate Identity */
const CorporateIdentityBlock = () => (
  <div className="gf-block gf-block--identity" aria-labelledby="footer-corp-heading">
    <div className="gf-logo" aria-label="PWWCapital home">
      <PwwLogoIcon size={36} variant="dark" />
      <span className="gf-logo-name">PWW Capital</span>
    </div>
    <span className="gf-corp-name">{CORPORATE.legalName}</span>
    <p className="gf-corp-desc">{CORPORATE.description}</p>
    <address className="gf-address" aria-label="Global headquarters">
      <span>{CORPORATE.address.line1}</span>
      <span>{CORPORATE.address.line2}</span>
      <span>{CORPORATE.address.line3}</span>
    </address>
    <p className="gf-reg-numbers" aria-label="Registration numbers">
      {CORPORATE.registrationNumbers.map((r, i) => (
        <span key={i} className="gf-reg-item">{r}</span>
      ))}
    </p>
    <p className="gf-global-presence">{CORPORATE.globalPresence}</p>
  </div>
);

/** Section 2: Account access notes — text only, no icons */
const RegulatoryBlock = () => (
  <div className="gf-block gf-block--regulatory" aria-labelledby="footer-reg-heading">
    <h3 id="footer-reg-heading" className="gf-block-heading">Account Access</h3>
    <ul className="gf-reg-list">
      {REGULATORY_ITEMS.map((item, i) => (
        <li key={i} className="gf-reg-list-item">{item}</li>
      ))}
    </ul>
    <ul className="gf-badge-list" aria-label="Compliance certifications">
      {COMPLIANCE_BADGES.map((badge, i) => (
        <li key={i} className="gf-badge">{badge}</li>
      ))}
    </ul>
  </div>
);

/** Section 3: Security & Fraud Prevention */
const SecurityBlock = () => (
  <div className="gf-block gf-block--security" aria-labelledby="footer-sec-heading">
    <h3 id="footer-sec-heading" className="gf-block-heading">Security &amp; Fraud Prevention</h3>
    <nav aria-label="Security resources">
      <ul className="gf-link-list">
        {SECURITY_LINKS.map((item, i) => (
          <li key={i}>
            <Link to={item.to} className="gf-link">{item.label}</Link>
          </li>
        ))}
      </ul>
    </nav>
  </div>
);

/** Section 4: Platform links */
const BankingProductsBlock = () => (
  <div className="gf-block gf-block--products" aria-labelledby="footer-prod-heading">
    <h3 id="footer-prod-heading" className="gf-block-heading">Platform</h3>
    <nav aria-label="Platform links">
      <ul className="gf-link-list">
        {BANKING_PRODUCTS.map((item, i) => (
          <li key={i}>
            <Link to={item.to} className="gf-link">{item.label}</Link>
          </li>
        ))}
      </ul>
    </nav>
  </div>
);

/** Section 5: Legal & Policy Documents */
const LegalBlock = () => (
  <div className="gf-block gf-block--legal" aria-labelledby="footer-legal-heading">
    <h3 id="footer-legal-heading" className="gf-block-heading">Legal &amp; Policies</h3>
    <nav aria-label="Legal documents">
      <ul className="gf-link-list">
        {LEGAL_LINKS.map((item, i) => (
          <li key={i}>
            {item.external ? (
              <a
                href={item.to}
                className="gf-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${item.label} (opens in new tab)`}
              >
                {item.label}
              </a>
            ) : (
              <Link to={item.to} className="gf-link">{item.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  </div>
);

/** Section 6: Bottom Bar */
const BottomBar = () => {
  const [region, setRegion]   = useState('US');
  const [language, setLanguage] = useState('en');
  const year = new Date().getFullYear();

  return (
    <div className="gf-bottom-bar" role="contentinfo" aria-label="Footer bottom bar">
      <div className="gf-bottom-left">
        <p className="gf-copyright">
          &copy; {year} PWWCapital™ · All Rights Reserved
        </p>
        <p className="gf-trademark">
          PWWCapital™ is a registered trademark. Unauthorised reproduction is prohibited.
        </p>
      </div>

      <div className="gf-bottom-selectors">
        <label htmlFor="gf-region-select" className="gf-selector-label">Region</label>
        <select
          id="gf-region-select"
          className="gf-selector"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          aria-label="Select region"
        >
          {REGIONS.map((r) => (
            <option key={r.code} value={r.code}>{r.name}</option>
          ))}
        </select>

        <label htmlFor="gf-lang-select" className="gf-selector-label">Language</label>
        <select
          id="gf-lang-select"
          className="gf-selector"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          aria-label="Select language"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

/* ── Main export ──────────────────────────────────────────────────────────── */

const GlobalFooter = () => (
  <footer className="global-footer" role="contentinfo" aria-label="PWWCapital global footer">

    {/* Regulatory disclosure banner */}
    <div className="gf-disclosure-banner" role="note" aria-label="Regulatory disclaimer">
      <p>
        This site provides account access and support tools for approved users.
        Public account creation is disabled. Feature availability depends on
        account status, internal review, and current published terms.
      </p>
    </div>

    {/* Divider */}
    <div className="gf-rule" aria-hidden="true" />

    {/* Main grid — 5 columns on desktop */}
    <div className="gf-main-grid">
      <CorporateIdentityBlock />
      <RegulatoryBlock />
      <SecurityBlock />
      <BankingProductsBlock />
      <LegalBlock />
    </div>

    {/* Divider */}
    <div className="gf-rule" aria-hidden="true" />

    {/* Divider */}
    <div className="gf-rule" aria-hidden="true" />

    {/* Bottom bar — Section 6 */}
    <BottomBar />
  </footer>
);

export default GlobalFooter;
